// feature/insumos-validity — Reparto/Admin queue for cases the abogado returned because
// insumos were invalid. Three actions: pedir más insumos / aceptar y enviar
// al abogado / cerrar como N/A. SLA resumes on every transition out.
import React, { useEffect, useState } from "react";
import { cases as casesApi } from "../../api/endpoints";
import { api } from "../../api/client";
import Spinner from "../../components/Spinner";
import Empty from "../../components/Empty";
import Confirm from "../../components/Confirm";
import { TipoPill } from "../../components/Pills";
import { useToast } from "../../components/Toast";
import { useCaseLauncher } from "../../components/CaseLauncher";
import { useAuth } from "../../auth/AuthContext";
import { useRefreshOnNotification } from "../../components/useRefreshOnNotification";
import { rolesOf } from "../../utils/permissions";

const fmt = (d) => d ? new Date(d).toLocaleString("es-CO") : "—";

export default function Devueltos() {
  const toast = useToast();
  const launcher = useCaseLauncher();
  const { user } = useAuth();
  // feature/single-role-audit — multi-role aware so Tania (admin/
  // carpetas/directora, primary admin) keeps the directora path here.
  const isDirectora = rolesOf(user).includes("directora");
  const [data, setData] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // {case, action}

  function load() {
    setData(null);
    api.get("/api/admin/devueltos").then(r => setData(r.data)).catch(() => setData([]));
  }
  useEffect(() => { load(); }, []);
  useRefreshOnNotification(["case_returned","case.devuelto_resuelto"], load);

  async function doAction() {
    if (!confirmAction) return;
    const { c, action } = confirmAction;
    try {
      await casesApi.action(c.id, action);
      toast.push("Acción aplicada");
      setConfirmAction(null);
      load();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error", "error");
    }
  }

  const actionLabel = {
    reroute_to_insumos: "Pedir más insumos",
    override_to_abogado: "Aceptar y enviar al abogado",
    close_as_na: "Cerrar como N/A",
    resolve_devuelto: "Marcar como resuelto",
  };

  return (
    <div className="col" style={{ gap: 16 }}>
      <h1 style={{ margin: 0 }}>Casos devueltos por insumos inválidos</h1>
      <div className="muted" style={{ marginTop: -8 }}>
        Cuando el abogado marca los insumos recibidos como inválidos, el caso aparece aquí.
        Decida si pedir más insumos, aceptar lo recibido y enviarlo al abogado, o cerrar como N/A.
      </div>

      <div className="card" style={{ padding: 0 }}>
        {data == null ? (
          <div style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
        ) : !data.length ? (
          <Empty title="Sin casos devueltos" message="Todo está fluyendo." />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Caso</th><th>Tipo de actuación</th><th>Accionante</th><th>Abogado</th>
                <th>Motivo</th><th>Días pendiente</th><th></th>
              </tr>
            </thead>
            <tbody>
              {data.map(c => (
                <tr key={c.id}>
                  <td onClick={() => launcher.open(c.id)} style={{ cursor: "pointer" }}>
                    <strong>{c.case_number}</strong>
                  </td>
                  <td><TipoPill tipo={c.tipo} /></td>
                  <td>{c.accionante_nombre || "—"}</td>
                  <td>{c.abogado_nombre || "—"}</td>
                  <td style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={c.insumos_invalidos_motivo || ""}>
                    {c.insumos_invalidos_motivo || "—"}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>{c.dias_pendiente ?? 0} día(s)</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {isDirectora ? (
                      <button className="sm" onClick={() => setConfirmAction({ c, action: "resolve_devuelto" })}>
                        Marcar resuelto
                      </button>
                    ) : (
                      <>
                        <button className="ghost sm" onClick={() => setConfirmAction({ c, action: "reroute_to_insumos" })}>Pedir más insumos</button>
                        <button className="ghost sm" onClick={() => setConfirmAction({ c, action: "override_to_abogado" })}>Aceptar</button>
                        <button className="ghost sm" style={{ color: "var(--color-danger)" }}
                                onClick={() => setConfirmAction({ c, action: "close_as_na" })}>Cerrar N/A</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Confirm
        open={!!confirmAction}
        title={confirmAction ? actionLabel[confirmAction.action] : ""}
        message={confirmAction
          ? `¿${actionLabel[confirmAction.action]} en ${confirmAction.c.case_number}? El reloj SLA se reanuda al salir de "devuelto a reparto".`
          : ""}
        onClose={() => setConfirmAction(null)}
        onConfirm={doAction}
        confirmText="Aplicar"
        danger={confirmAction?.action === "close_as_na"}
      />
    </div>
  );
}
